import { useRef, useState, TouchEvent } from "react";

interface CaseStudy {
  id: string;
  title: string;
  scope: string;
  summary: string;
  metrics: { value: string; label: string }[];
}

const caseStudies: CaseStudy[] = [
  {
    id: "facility-5s-turnaround",
    title: "40,000 Sq. Ft. Facility Turnaround & 5S Implementation",
    scope: "40,000 sq ft facility · 12-person team · 3-week engagement",
    summary:
      "A high-volume industrial storage facility faced excessive material search times, underused vertical storage, and open safety and compliance exposure. Through cube utilisation analysis, a visual factory system with alphanumeric location labelling, and DMAIC root-cause work, the space was transformed into an organised, audit-ready operation.",
    metrics: [
      { value: "90%", label: "Faster material retrieval (20 min → 2 min)" },
      { value: "$142,000", label: "Annual labour savings" },
      { value: "9", label: "Critical safety hazards eliminated" },
    ],
  },
];

export default function CaseStudies() {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const study = caseStudies[selectedIndex];
  const showControls = caseStudies.length > 1;

  const handlePrev = () => {
    setSelectedIndex((prev) => (prev === 0 ? 0 : prev - 1));
  };

  const handleNext = () => {
    setSelectedIndex((prev) =>
      prev === caseStudies.length - 1 ? caseStudies.length - 1 : prev + 1
    );
  };

  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null || !showControls) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) >= 50) {
      if (delta < 0) handleNext();
      else handlePrev();
    }
    touchStartX.current = null;
  };

  return (
    <div className="container relative py-16 lg:py-36 flex lg:flex-row flex-col">
      <div className="justify-start items-start flex-col md:flex-row flex gap-16 lg:gap-36 w-full">
        <div className="relative flex-col min-w-[275px] justify-start items-start gap-2 inline-flex">
          <div className="p-2 bg-[#e6f1ff] rounded-md justify-center items-center gap-2 inline-flex">
            <div className="whitespace-nowrap text-[#2b8cff] text-base font-medium font-secondary leading-[19px]">
              CASE STUDIES
            </div>
          </div>
          <div className="text-[#0e0e0e] text-2xl md:text-[32px] font-semibold font-primary leading-[41.60px]">
            Proven Results <br /> in the Field
          </div>
        </div>

        <div
          className="flex-col relative justify-start items-start gap-6 inline-flex w-full"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="flex-col justify-start items-start gap-2 flex">
            <div className="text-[#0e0e0e] text-xl font-normal font-primary leading-7">
              {study.title}
            </div>
            <div className="text-[#64748b] text-sm font-light font-primary leading-5 tracking-[0.01em]">
              {study.scope}
            </div>
          </div>

          <div className="text-[#373737] lg:text-xl font-light font-primary leading-7 tracking-[0.01em]">
            {study.summary}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
            {study.metrics.map((metric) => (
              <div
                key={metric.label}
                className="p-5 bg-gray-50 rounded-md flex-col justify-start items-start gap-2 flex"
              >
                <div className="text-[#0075ff] text-2xl font-semibold font-primary leading-8">
                  {metric.value}
                </div>
                <div className="text-[#373737] text-sm font-light font-primary leading-[19.60px] tracking-[0.01em]">
                  {metric.label}
                </div>
              </div>
            ))}
          </div>

          {showControls && (
            <div className="flex items-center gap-5 mt-2">
              <img
                src="https://storage.googleapis.com/uxpilot-auth.appspot.com/113049385656753869919/Frame75-cbbd7b71-4f2b-4789-82ce-7959777b929c.svg"
                alt="Previous case study"
                onClick={handlePrev}
                className={`cursor-pointer ${
                  selectedIndex === 0 ? "opacity-50" : "opacity-100"
                }`}
              />
              <img
                src="https://storage.googleapis.com/uxpilot-auth.appspot.com/113049385656753869919/Frame76-976c95e5-582b-48b6-b9b4-37b80a081f7c.svg"
                alt="Next case study"
                onClick={handleNext}
                className={`cursor-pointer ${
                  selectedIndex === caseStudies.length - 1
                    ? "opacity-50"
                    : "opacity-100"
                }`}
              />
              <div className="text-[#373737] text-base font-light font-primary leading-[22.40px] tracking-[0.01em]">
                {String(selectedIndex + 1).padStart(2, "0")} /{" "}
                {String(caseStudies.length).padStart(2, "0")}
              </div>
            </div>
          )}
        </div>
      </div>

      <img
        src="https://storage.googleapis.com/uxpilot-auth.appspot.com/113049385656753869919/Rectangle1-f34482f9-081e-41f5-906b-30ec5104279c.svg"
        alt=""
        className="absolute top-0 left-[22px] w-10 md:w-auto"
      />
    </div>
  );
}
